import React, { useState } from 'react';
import { 
  Globe, 
  Plus, 
  Trash2, 
  Settings, 
  Play, 
  Square, 
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { useStream } from '../context/StreamContext';

function RestreamManager() {
  const { streams, restreamTargets, addRestreamTarget } = useStream();
  
  const [selectedStream, setSelectedStream] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTarget, setNewTarget] = useState({
    platform: 'youtube',
    name: '',
    url: '',
    streamKey: '',
    enabled: true
  });
  const [showStreamKeys, setShowStreamKeys] = useState({});

  const platforms = [
    { 
      id: 'youtube', 
      name: 'YouTube Live', 
      icon: '📺',
      color: 'bg-red-500',
      rtmpUrl: 'rtmp://a.rtmp.youtube.com/live2/',
      description: 'Stream to YouTube Live'
    },
    { 
      id: 'twitch', 
      name: 'Twitch', 
      icon: '🎮',
      color: 'bg-purple-500',
      rtmpUrl: 'rtmp://live.twitch.tv/app/',
      description: 'Stream to Twitch'
    },
    { 
      id: 'facebook', 
      name: 'Facebook Live', 
      icon: '👥',
      color: 'bg-blue-500',
      rtmpUrl: 'rtmps://live-api-s.facebook.com:443/rtmp/',
      description: 'Stream to Facebook Live'
    },
    { 
      id: 'linkedin', 
      name: 'LinkedIn Live', 
      icon: '💼',
      color: 'bg-blue-600',
      rtmpUrl: 'rtmps://1391176877.contribute.live-video.net/app/',
      description: 'Stream to LinkedIn Live'
    },
    { 
      id: 'custom', 
      name: 'Custom RTMP', 
      icon: '⚙️',
      color: 'bg-gray-500',
      rtmpUrl: '',
      description: 'Custom RTMP endpoint'
    }
  ];

  const mockRestreamTargets = [
    {
      id: 'restream-1',
      platform: 'youtube',
      name: 'My YouTube Channel',
      url: 'rtmp://a.rtmp.youtube.com/live2/',
      streamKey: 'xxxx-xxxx-xxxx-xxxx',
      enabled: true,
      status: 'connected',
      viewers: 1250,
      uptime: 3600
    },
    {
      id: 'restream-2',
      platform: 'twitch',
      name: 'My Twitch Channel',
      url: 'rtmp://live.twitch.tv/app/',
      streamKey: 'live_123456789_abcdefghijk',
      enabled: true,
      status: 'connected',
      viewers: 890,
      uptime: 3600
    },
    {
      id: 'restream-3',
      platform: 'facebook',
      name: 'Company Facebook',
      url: 'rtmps://live-api-s.facebook.com:443/rtmp/',
      streamKey: 'FB-123456789-0-ABcdEfGhIjKl',
      enabled: false,
      status: 'disconnected',
      viewers: 0,
      uptime: 0
    }
  ];

  const handleAddTarget = async (e) => {
    e.preventDefault();
    if (!selectedStream || !newTarget.platform || !newTarget.streamKey) return;

    try {
      const platform = platforms.find(p => p.id === newTarget.platform);
      const targetData = {
        platform: newTarget.platform,
        name: newTarget.name || `${platform.name} Stream`,
        url: newTarget.platform === 'custom' ? newTarget.url : platform.rtmpUrl,
        streamKey: newTarget.streamKey,
        enabled: newTarget.enabled
      };

      await addRestreamTarget(selectedStream, targetData);
      
      // Reset form
      setNewTarget({
        platform: 'youtube',
        name: '',
        url: '',
        streamKey: '',
        enabled: true
      });
      setShowAddModal(false);
    } catch (error) {
      console.error('Failed to add restream target:', error);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const toggleStreamKeyVisibility = (targetId) => {
    setShowStreamKeys(prev => ({
      ...prev,
      [targetId]: !prev[targetId]
    }));
  };

  const formatUptime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'connecting': return <AlertCircle className="w-5 h-5 text-yellow-400" />;
      case 'error': return <XCircle className="w-5 h-5 text-red-400" />;
      default: return <XCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'text-green-400';
      case 'connecting': return 'text-yellow-400';
      case 'error': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Restream Manager</h1>
          <p className="text-gray-400">
            Broadcast your stream to multiple platforms simultaneously
          </p>
        </div>
        
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Add Platform</span>
        </button>
      </div>

      {/* Stream Selection */}
      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">Select Source Stream</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Source Stream
            </label>
            <select
              value={selectedStream}
              onChange={(e) => setSelectedStream(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-cf-orange focus:border-transparent"
            >
              <option value="">Select a stream...</option>
              {streams.map(stream => (
                <option key={stream.id} value={stream.id}>
                  {stream.name} ({stream.status})
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-end">
            <div className="text-sm text-gray-400">
              <p>Select the stream you want to rebroadcast to other platforms.</p>
              <p>The stream must be live to enable restreaming.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Cards */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-6">Connected Platforms</h2>
        
        {mockRestreamTargets.length === 0 ? (
          <div className="card text-center py-12">
            <Globe className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-300 mb-2">No platforms connected</h3>
            <p className="text-gray-500 mb-6">Add your first restreaming platform to get started</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary"
            >
              Add Your First Platform
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {mockRestreamTargets.map((target) => {
              const platform = platforms.find(p => p.id === target.platform);
              
              return (
                <div key={target.id} className="card group hover:shadow-xl transition-all duration-300">
                  {/* Platform Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 ${platform?.color || 'bg-gray-500'} rounded-lg flex items-center justify-center text-white text-xl`}>
                        {platform?.icon || '⚙️'}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">{target.name}</h3>
                        <p className="text-gray-400 text-sm">{platform?.name || 'Custom Platform'}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(target.status)}
                      <span className={`text-sm font-medium ${getStatusColor(target.status)}`}>
                        {target.status.charAt(0).toUpperCase() + target.status.slice(1)}
                      </span>
                    </div>
                  </div>

                  {/* Platform Stats */}
                  {target.status === 'connected' && (
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-gray-700 rounded-lg p-3">
                        <p className="text-gray-400 text-xs">Viewers</p>
                        <p className="text-white text-lg font-semibold">{target.viewers.toLocaleString()}</p>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-3">
                        <p className="text-gray-400 text-xs">Uptime</p>
                        <p className="text-white text-lg font-semibold">{formatUptime(target.uptime)}</p>
                      </div>
                    </div>
                  )}

                  {/* Stream Details */}
                  <div className="space-y-3 mb-4">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-gray-400 text-sm">RTMP URL</p>
                        <button
                          onClick={() => copyToClipboard(target.url)}
                          className="text-cf-orange hover:text-orange-400 transition-colors duration-200"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      <code className="block w-full text-xs bg-gray-700 px-3 py-2 rounded text-gray-300 break-all">
                        {target.url}
                      </code>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-gray-400 text-sm">Stream Key</p>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => toggleStreamKeyVisibility(target.id)}
                            className="text-gray-400 hover:text-white transition-colors duration-200"
                          >
                            {showStreamKeys[target.id] ? 
                              <EyeOff className="w-4 h-4" /> : 
                              <Eye className="w-4 h-4" />
                            }
                          </button>
                          <button
                            onClick={() => copyToClipboard(target.streamKey)}
                            className="text-cf-orange hover:text-orange-400 transition-colors duration-200"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <code className="block w-full text-xs bg-gray-700 px-3 py-2 rounded text-gray-300 break-all">
                        {showStreamKeys[target.id] 
                          ? target.streamKey 
                          : '•'.repeat(target.streamKey.length)
                        }
                      </code>
                    </div>
                  </div>

                  {/* Platform Actions */}
                  <div className="flex items-center space-x-2 pt-3 border-t border-gray-700">
                    <button
                      className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                        target.enabled
                          ? 'bg-red-500 hover:bg-red-600 text-white'
                          : 'bg-green-500 hover:bg-green-600 text-white'
                      }`}
                    >
                      {target.enabled ? (
                        <>
                          <Square className="w-4 h-4 inline mr-2" />
                          Stop
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 inline mr-2" />
                          Start
                        </>
                      )}
                    </button>
                    
                    <button className="p-2 text-gray-400 hover:text-white transition-colors duration-200">
                      <Settings className="w-4 h-4" />
                    </button>
                    
                    <button className="p-2 text-gray-400 hover:text-red-400 transition-colors duration-200">
                      <Trash2 className="w-4 h-4" />
                    </button>
                    
                    <button className="p-2 text-gray-400 hover:text-white transition-colors duration-200">
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Platform Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg">
            <h3 className="text-xl font-bold text-white mb-6">Add Streaming Platform</h3>
            
            <form onSubmit={handleAddTarget} className="space-y-4">
              {/* Platform Selection */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Platform
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {platforms.map(platform => (
                    <button
                      key={platform.id}
                      type="button"
                      onClick={() => setNewTarget(prev => ({ 
                        ...prev, 
                        platform: platform.id,
                        url: platform.rtmpUrl 
                      }))}
                      className={`flex items-center space-x-3 p-3 rounded-lg border-2 transition-all duration-200 ${
                        newTarget.platform === platform.id
                          ? 'border-cf-orange bg-cf-orange bg-opacity-10'
                          : 'border-gray-600 hover:border-gray-500'
                      }`}
                    >
                      <div className={`w-8 h-8 ${platform.color} rounded-lg flex items-center justify-center text-white text-sm`}>
                        {platform.icon}
                      </div>
                      <div className="text-left">
                        <p className="text-white font-medium text-sm">{platform.name}</p>
                        <p className="text-gray-400 text-xs">{platform.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Platform Name */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  value={newTarget.name}
                  onChange={(e) => setNewTarget(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-cf-orange focus:border-transparent"
                  placeholder="e.g., My YouTube Channel"
                />
              </div>

              {/* Custom URL (for custom platform) */}
              {newTarget.platform === 'custom' && (
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    RTMP URL
                  </label>
                  <input
                    type="url"
                    value={newTarget.url}
                    onChange={(e) => setNewTarget(prev => ({ ...prev, url: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-cf-orange focus:border-transparent"
                    placeholder="rtmp://your-server.com/live/"
                    required
                  />
                </div>
              )}

              {/* Stream Key */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Stream Key
                </label>
                <input
                  type="password"
                  value={newTarget.streamKey}
                  onChange={(e) => setNewTarget(prev => ({ ...prev, streamKey: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-cf-orange focus:border-transparent"
                  placeholder="Enter your stream key..."
                  required
                />
                <p className="text-gray-500 text-xs mt-1">
                  Get this from your platform's streaming settings
                </p>
              </div>

              {/* Enable by default */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={newTarget.enabled}
                  onChange={(e) => setNewTarget(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="w-4 h-4 text-cf-orange bg-gray-700 border-gray-600 rounded focus:ring-cf-orange focus:ring-2"
                />
                <label htmlFor="enabled" className="ml-2 text-gray-300 text-sm">
                  Enable streaming to this platform by default
                </label>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={!newTarget.platform || !newTarget.streamKey || !selectedStream}
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Platform
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default RestreamManager; 